import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid2 as Grid,
  Paper,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography
} from '@mui/material'
import { Field, FieldProps, Form, Formik } from 'formik'
import React, { Fragment, useEffect } from 'react'
import * as Yup from 'yup'
import { VasRuleInfo, VasRuleUpdate } from '../../../interfaces/VasRule/VasRule'
import { eTypePackage } from '../../../enums/eTypePackage'
import { TypeVasDDL } from '../../../components/TypeVas/TypeVasDDL'
import { FlowVasDDL } from '../../../components/TypeVas/FlowVasDDL'
import { TypeValidationVasDDL } from '../../../components/TypeVas/TypeValidationVasDDL'
import { useApi } from '../../../hooks/useApi'
import { getVasRule, patchVasRule } from '../config/api'
import { Status } from '../../../hooks/useFetchState'
import { Loading } from '../../../components/UI'
import { useSnackbar } from '../../../hooks/useSnackbar'
import CustomAlert from '../../../components/_WMS/AlertMessage/AlertMessage'

interface EditVasRuleModalProps {
  vasRuleId: number
  handleSnackbar: (message: string, success: boolean) => void
  handleCloseModal: () => void
  onUpdateSuccess?: () => void
}

const validationSchema = Yup.object().shape({
  vasRuleName: Yup.string().trim().required('Nombre de la regla es requerido')
})

export const EditVasRuleModal: React.FC<EditVasRuleModalProps> = ({
  vasRuleId,
  handleSnackbar,
  handleCloseModal,
  onUpdateSuccess
}) => {
  const { openSnackBar } = useSnackbar()
  const { state: requestVasRuleState, callApi: requestGetVasRule } =
    useApi<VasRuleInfo>()
  const { callApi: requestUpdateVasRule } = useApi()

  const vasRuleData = (vasRuleId: number) => {
    requestGetVasRule(getVasRule(vasRuleId)).catch((error) => {
      openSnackBar(error, false)
    })
  }

  // Se devuelve la promesa para que Formik resetee `isSubmitting` cuando la
  // request termina; si no, el botón queda en 'Procesando...' tras un error.
  const handleSubmit = (values: any) => {
    const dto: VasRuleUpdate = {
      vasRuleName: `${values.vasRuleName}`,
      typePackageId: values.typePackageId,
      active: values.active
    }
    return requestUpdateVasRule(patchVasRule(vasRuleId, dto))
      .then(() => {
        handleSnackbar(
          `Actualización realizada exitosamente: ${values.vasRuleName}`,
          true
        )
        onUpdateSuccess?.()
        handleCloseModal()
      })
      .catch((error) => {
        handleSnackbar(error, false)
      })
  }

  useEffect(() => {
    vasRuleData(vasRuleId)
  }, [vasRuleId])

  const typePackageOptions = Object.entries(eTypePackage)
    .filter(([key]) => isNaN(Number(key)))
    .filter(([_, value]) => {
      const numeric = Number(value as any)
      return !Number.isNaN(numeric) && numeric === eTypePackage.Unidad
    })
    .map(([key, value]) => ({
      label: key,
      value: String(value)
    }))


  if (
    requestVasRuleState.status === Status.Loading ||
    !requestVasRuleState.data
  ) {
    return (
      <div>
        <Typography>
          <Loading />
        </Typography>
      </div>
    )
  }

  return (
    <Fragment>
      <Formik
        initialValues={requestVasRuleState.data}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isValid, dirty, isSubmitting }) => {
          return (
            <Form autoComplete='off'>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <CustomAlert
                    severity='warning'
                    message='Los datos registrados reemplazarán los existentes'
                    maxWidth={'100%'}
                  />
                </Grid>
                <Grid size={12}>
                  <Typography>Nombre de la Regla *</Typography>
                  <Field name='vasRuleName'>
                    {({ field, meta }: FieldProps<string>) => (
                      <TextField
                        {...field}
                        fullWidth
                        variant='outlined'
                        error={meta.touched && Boolean(meta.error)}
                        helperText={meta.touched && meta.error}
                      />
                    )}
                  </Field>
                </Grid>
                <Grid size={6}>
                  <Typography>Tipo de VAS *</Typography>
                  <Field name='typeVasId'>
                    {({ field, form }: FieldProps<number>) => (
                      <TypeVasDDL
                        value={field.value}
                        onChange={(e: any) =>
                          form.setFieldValue(field.name, e.target.value)
                        }
                        type='outlined'
                        fullWidth
                        required={true}
                        disabled
                      />
                    )}
                  </Field>
                </Grid>

                <Grid size={6}>
                  <Field name='typePackageId'>
                    {({ field, form }: FieldProps<number>) => (
                      <FormControl fullWidth>
                        <Typography>Tipo de Empaque *</Typography>

                        <RadioGroup
                          row
                          value={String(field.value)}
                          onChange={(e) =>
                            form.setFieldValue(
                              field.name,
                              Number(e.target.value)
                            )
                          }
                        >
                          {typePackageOptions.map((opt) => (
                            <FormControlLabel
                              key={opt.value}
                              value={opt.value}
                              control={<Radio />}
                              label={opt.label}
                              disabled
                            />
                          ))}
                        </RadioGroup>
                      </FormControl>
                    )}
                  </Field>
                </Grid>
                <Grid size={6}>
                  <Typography>Modo de Validación *</Typography>
                  <Field name='typeValidationVasId'>
                    {({ field, form }: FieldProps<number>) => (
                      <TypeValidationVasDDL
                        value={field.value}
                        onChange={(e: any) =>
                          form.setFieldValue(field.name, e.target.value)
                        }
                        fullWidth
                        required
                        type='outlined'
                        disabled
                      />
                    )}
                  </Field>
                </Grid>
                <Grid size={6}>
                  <Typography>Proceso de Aplicación *</Typography>
                  <Field name='flowVasId'>
                    {({ field, form }: FieldProps<number>) => (
                      <FlowVasDDL
                        value={field.value}
                        onChange={(e: any) =>
                          form.setFieldValue(field.name, e.target.value)
                        }
                        fullWidth
                        required
                        type='outlined'
                        disabled
                      />
                    )}
                  </Field>
                </Grid>
                <Grid size={6}>
                  <Paper
                    variant='outlined'
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: 6
                    }}
                  >
                    <Box
                      display='flex'
                      alignItems='center'
                      justifyContent='space-between'
                    >
                      {/* Textos a la izquierda */}
                      <Box>
                        <Typography variant='subtitle1'>Estado</Typography>
                        <Typography variant='body2' color='textSecondary'>
                          Activar o Desactivar esta regla
                        </Typography>
                      </Box>

                      {/* Switch a la derecha */}
                      <Field name='active'>
                        {({ field, form }: FieldProps<boolean>) => (
                          <Switch
                            checked={field.value}
                            onChange={(e) =>
                              form.setFieldValue(field.name, e.target.checked)
                            }
                            sx={switchSx}
                          />
                        )}
                      </Field>
                    </Box>
                  </Paper>
                </Grid>

                <Grid size={6}>
                  <Paper
                    variant='outlined'
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: 6
                    }}
                  >
                    <Box
                      display='flex'
                      alignItems='center'
                      justifyContent='space-between'
                    >
                      {/* Textos a la izquierda */}
                      <Box>
                        <Typography variant='subtitle1'>
                          Obligatorio / Bloqueante
                        </Typography>
                        <Typography variant='body2' color='textSecondary'>
                          Bloquea el proceso si no se completa
                        </Typography>
                      </Box>

                      {/* Switch a la derecha */}
                      <Field name='isMandatory'>
                        {({ field, form }: FieldProps<boolean>) => (
                          <Switch
                            checked={field.value}
                            onChange={(e) =>
                              form.setFieldValue(field.name, e.target.checked)
                            }
                            sx={switchSx}
                            disabled
                          />
                        )}
                      </Field>
                    </Box>
                  </Paper>
                </Grid>

                <Grid
                  size={12}
                  display='flex'
                  gap={2}
                  justifyContent='flex-end'
                >
                  <Button
                    variant='outlined'
                    color='primary'
                    onClick={handleCloseModal}
                    disabled={isSubmitting}
                    sx={{ borderRadius: '12px' }}
                  >
                    Cerrar
                  </Button>
                  <Button
                    type='submit'
                    variant='contained'
                    color='primary'
                    disabled={!isValid || !dirty || isSubmitting}
                    sx={{ borderRadius: '12px' }}
                    startIcon={
                      isSubmitting ? (
                        <CircularProgress size={20} color='inherit' />
                      ) : undefined
                    }
                  >
                    {isSubmitting ? 'Procesando...' : 'Guardar'}
                  </Button>
                </Grid>
              </Grid>
            </Form>
          )
        }}
      </Formik>
    </Fragment>
  )
}

const switchSx = {
  padding: '8px',
  '& .MuiSwitch-switchBase': {
    padding: '10px',
    '&.Mui-checked': {
      transform: 'translateX(20px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: 'primary.light',
        opacity: 1
      }
    }
  },
  '& .MuiSwitch-track': {
    borderRadius: 20,
    backgroundColor: '#ccc',
    opacity: 1,
    height: 22,
    width: 42
  },
  '& .MuiSwitch-thumb': {
    width: 18,
    height: 18
  }
}
